import { default as classNames, default as classnames } from "classnames";
import React, { ChangeEvent, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { useOutsideClick } from "../../hooks/useOutsideClick";
import style from "../../scss/export.module.scss";
import { ChevronDownIcon, ChevronUpIcon } from "../Icons";
import "./select.scss";

enum SelectSize {
    "small",
    "medium",
}

export enum SelectType {
    "primary",
    "secondary",
    "standard",
}

export type TSelect = {
    label?: string | JSX.Element;
    name?: string;
    options: Array<TSelectOption>;
    defaultValue?: string | number;
    openIcon?: JSX.Element;
    closedIcon?: JSX.Element;
    isValid?: boolean;
    type?: keyof typeof SelectType;
    disabled?: boolean;
    fullWidth?: boolean;
    onSelectCallback?: Function | null;
    onCloseCallback?: Function | null;
    size?: keyof typeof SelectSize | null;
    useFirstAsDefault?: boolean;
    placeholder?: string;
    onDarkBackground?: boolean;
    onSelectCallbackWithEvent?: (arg: ChangeEvent<HTMLSelectElement>) => void;
    required?: boolean;
    showAsterisk?: boolean;
    boldLabel?: boolean;
    hideSelectedFromOptions?: boolean;
    showPlaceholderInOptions?: boolean;
    ariaLabel?: string;
    errorMessage?: string;
};

export type TSelectOption = {
    value: string | number;
    label?: string;
    className?: string;
    disabled?: boolean;
};

export const Select = ({
    label,
    name,
    options,
    defaultValue,
    openIcon,
    isValid,
    closedIcon = openIcon,
    type = "primary",
    disabled = false,
    fullWidth = false,
    onSelectCallback = null,
    onCloseCallback = null,
    size = null,
    useFirstAsDefault = true,
    placeholder,
    onDarkBackground = false,
    onSelectCallbackWithEvent,
    required = false,
    showAsterisk = false,
    boldLabel = false,
    hideSelectedFromOptions,
    showPlaceholderInOptions = false,
    ariaLabel,
    errorMessage,
}: TSelect) => {
    const selectOptionsId = useId();
    const [selected, setSelected] = useState<TSelectOption | null>(
        options.find(({ value }) => value === defaultValue) || (useFirstAsDefault ? options[0] : null)
    );

    useEffect(() => {
        const defaultOption = options.find(({ value }) => value === defaultValue);
        if (defaultOption) setSelected(defaultOption);
        else setSelected(useFirstAsDefault ? options[0] : null);
        // eslint-disable-next-line
    }, [defaultValue, useFirstAsDefault]);

    useEffect(() => {
        setSelected(current => {
            const selected = options.find(({ value }) => value === current?.value);
            if (!selected) {
                const defaultOption = options.find(({ value }) => value === defaultValue);
                return defaultOption || null;
            }
            return current;
        });
        // eslint-disable-next-line
    }, []);

    const [isOpen, setIsOpen] = useState(false);
    const arrowColor = type === "secondary" ? style.whiteColor : style.brandBlueColor;

    const classNameSelectWrapper = classnames("c-select", {
        "c-select--full-width": fullWidth,
        "c-select--use-standard-select": type === "standard",
    });
    const classNameLabel = classnames("c-select__label", {
        "c-select__label--on-dark-bg": onDarkBackground,
        "c-select__label--bold": boldLabel,
    });
    const classNameSelectGroup = classnames("c-select__select-group", {
        "c-select__select-group--disabled": disabled,
    });
    const classNameSelect = classnames(
        "c-select__select",
        `c-select__select--${type}`,
        size && `c-select__select--${size}`,
        !selected && "c-select__select--show-placeholder",
        {
            "c-select__select--disabled": disabled,
            "c-select__select--success": isValid,
            "c-select__select--error": isValid === false,
        }
    );
    const classNameDropDown = classnames("c-select__options", {
        "c-select__options--disabled": disabled,
        "c-select__options--hidden": !isOpen,
    });
    const classNameOption = "c-select__option";
    const classNameErrorMessage = classnames("c-select__error-message", {
        "c-select__error-message--on-dark-bg": onDarkBackground,
    });
    const showIcon = type === "primary" || type === "secondary" || false;
    const wrapperRef = useRef<HTMLDivElement>(null);

    useOutsideClick(isOpen, wrapperRef, () => setIsOpen(false));

    const optionsRef = useRef<HTMLDivElement>(null);
    // const leftIconRef = useRef(null);
    const rightIconRef = useRef<HTMLDivElement>(null);
    const [buttonWidth, setButtonWidth] = useState<number | null>(null);

    const onLayout = (longestOptionLength: number, forceUpdate: boolean = false) => {
        // const leftIconWidth = leftIconRef.current ? leftIconRef.current.offsetWidth : 0;
        const rightIconWidth = rightIconRef.current ? rightIconRef.current.offsetWidth : 0;
        const totalButtonWidth = rightIconWidth + longestOptionLength + (showIcon ? 10 : 0);
        if (forceUpdate) {
            setButtonWidth(totalButtonWidth);
        }
    };

    const onFullWithResize = () => {
        const wrapperWidth = wrapperRef.current ? wrapperRef.current.offsetWidth : 0;
        setButtonWidth(wrapperWidth);
    };

    useLayoutEffect(() => {
        const longestOptionLength = optionsRef.current ? optionsRef.current.offsetWidth : 0;
        fullWidth ? onFullWithResize() : onLayout(longestOptionLength, true);
        // eslint-disable-next-line
    }, [showIcon, fullWidth]);

    const wrapperObserver = React.useRef(
        new ResizeObserver(() => {
            fullWidth && onFullWithResize();
        })
    );

    useEffect(() => {
        const observerRef = wrapperObserver.current;
        let observerRefValue: any = null;

        if (wrapperRef.current) {
            observerRef.observe(wrapperRef.current);
            observerRefValue = wrapperRef.current;
        }

        return () => {
            if (observerRefValue) {
                observerRef.unobserve(observerRefValue);
            }
        };
    }, [wrapperRef, wrapperObserver]);

    const onToggle = () => {
        setIsOpen(isOpen => !isOpen);
    };

    const onSelect = (selected: TSelectOption) => {
        setSelected(selected);
        onSelectCallback && onSelectCallback(selected);
        onCloseCallback && onCloseCallback();

        setIsOpen(false);
    };

    const onStandardSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
        let selected = options.find(option => option.value.toString() === e.target.value);

        if (!selected && placeholder && showPlaceholderInOptions) {
            selected = { value: e.target.value, label: placeholder };
        }

        if (selected) {
            setSelected(selected);
            onSelectCallback?.(selected);
            onSelectCallbackWithEvent?.(e);
            onCloseCallback?.();
        }
    };

    const calculatedDefaultValue = (option: TSelectOption | null) => {
        if (typeof option === "object" && !Array.isArray(option) && option !== null) {
            if ("label" in option) return option.label;
            if ("value" in option) return option.value;
        }
        return undefined;
    };

    const selectedValue = calculatedDefaultValue(selected);
    const standardSelectValue = selected?.value ?? "";
    const labelId = useId();

    return (
        <div className={classNameSelectWrapper}>
            {label && (
                <label id={labelId} className={classNameLabel}>
                    {label} {showAsterisk && <span className="c-select__asterisk">*</span>}
                </label>
            )}
            <select
                required={required}
                name={name}
                value={standardSelectValue}
                className={classNames("c-select__standard-select", {
                    "c-select__standard-select--disabled": disabled,
                    "c-select__standard-select--error": isValid === false,
                    "c-select__standard-select--success": isValid,
                })}
                onChange={onStandardSelect}
                aria-label={ariaLabel}
                aria-labelledby={label ? labelId : undefined}
                aria-errormessage={errorMessage}
            >
                {placeholder && (
                    <option value="" disabled={!showPlaceholderInOptions}>
                        {placeholder}
                    </option>
                )}
                {options.map((option: TSelectOption, i) => (
                    <option key={`${option.value}-${i}`} value={option.value} disabled={option.disabled}>
                        {option.label}
                    </option>
                ))}
            </select>
            {type !== "standard" && (
                <div className={classNameSelectGroup} ref={wrapperRef}>
                    <button
                        style={!fullWidth && buttonWidth !== null ? { width: buttonWidth || "auto" } : {}}
                        className={classNameSelect}
                        onClick={onToggle}
                        type="button"
                        {...(!label && ariaLabel ? { "aria-label": ariaLabel } : {})}
                        aria-expanded={isOpen}
                        aria-controls={selectOptionsId}
                    >
                        <div className="c-select__selected-option">
                            {selectedValue !== undefined ? selectedValue : placeholder}
                        </div>
                        {showIcon && (
                            <div ref={rightIconRef} style={{ display: "flex", alignItems: "center" }}>
                                {isOpen
                                    ? openIcon || <ChevronUpIcon color={arrowColor} />
                                    : closedIcon || <ChevronDownIcon color={arrowColor} />}
                            </div>
                        )}
                    </button>
                    <div
                        id={selectOptionsId}
                        style={buttonWidth !== null ? { width: buttonWidth } : {}}
                        className={classNameDropDown}
                        tabIndex={0}
                        ref={optionsRef}
                        aria-hidden={!isOpen}
                    >
                        {options
                            .filter(o => (hideSelectedFromOptions ? o.value !== selected?.value : true))
                            .map((option: TSelectOption, i) => (
                                <div
                                    className={classNameOption}
                                    key={`${option.value}-${i}`}
                                    onClick={() => onSelect(option)}
                                >
                                    {option.label || option.value}
                                </div>
                            ))}
                    </div>
                </div>
            )}
            {isValid === false && errorMessage && (
                <p role="alert" aria-live="assertive" className={classNameErrorMessage}>
                    {errorMessage}
                </p>
            )}
        </div>
    );
};
